import { MouseEvent, MutableRefObject } from 'react'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'

import { getCellId } from '@components/TimeLineGrid/helpers'

import { ACTION_CODE, ENTITY, EVENT_CODE, FORM_TYPE } from '@constants'
import {
  ActionRow,
  ConfiguredColumn,
  HandleTimelineCellContextMenu,
  HandleTimelineChangeSelectedCells,
  HandleTimelineToggleSelectedEvent,
  ObjectDataRecord,
  ObjectFieldDTO,
  TimelineCellContext,
  TimeLineDTOType,
} from '@types'

type UseHandlers = {
  config: TimeLineDTOType
  params: GridRenderCellParams
  cellContextRef: MutableRefObject<TimelineCellContext>
  objectXPKField: ObjectFieldDTO
  handleContextMenu: HandleTimelineCellContextMenu
  handleChangeSelectedCells: HandleTimelineChangeSelectedCells
  handleToggleSelectedEvent: HandleTimelineToggleSelectedEvent
  onClickWithDelay: (event: unknown, onClick: (event: unknown) => void) => void
  onDoubleClick: (event: unknown, onClick: (event: unknown) => void) => void
  handleExecuteActions: (data: {
    actions: ActionRow[]
    srcObj: ObjectDataRecord
    eventCode: EVENT_CODE
  }) => void
  objectXActionsObject: Record<EVENT_CODE, ActionRow>
  handleOpenTooltip: (
    event: MouseEvent<HTMLElement>,
    data: {
      srcObj?: ObjectDataRecord
      action: ActionRow
      column?: ConfiguredColumn
    }
  ) => Promise<void>
}

export const useHandlers = (props: UseHandlers) => {
  const {
    config,
    params,
    cellContextRef,
    objectXPKField,
    handleContextMenu,
    handleChangeSelectedCells,
    handleToggleSelectedEvent,
    onClickWithDelay,
    onDoubleClick,
    handleExecuteActions,
    objectXActionsObject,
    handleOpenTooltip,
  } = props

  const dates = params.field?.slice(1, -1)?.split(';')
  const cellId = getCellId(params.row.id, params.field)
  const isCellSelected =
    Boolean(cellContextRef.current.selectedCells[cellId]) &&
    cellContextRef.current.selectedCells[cellId].events.length === params.value?.length

  const getFormProps = (item?: ObjectDataRecord) => {
    return {
      event: FORM_TYPE.EDIT,
      entityType: ENTITY.VIEW,
      isIndependentForm: true,
      objectId: objectXPKField ? item?.[objectXPKField.name] : '',
    }
  }

  const handleOpenContextMenu = (
    event: MouseEvent<HTMLDivElement>,
    action: ActionRow,
    item?: ObjectDataRecord
  ) => {
    const isEventSelected = cellContextRef.current.selectedCells[cellId]?.events.some(
      e => e[objectXPKField.name] === item?.[objectXPKField.name]
    )

    handleContextMenu({
      event,
      action,
      params,
      cellItem: item,
      cellContextRef,
      isOpenedFromSelectedCell: isCellSelected || isEventSelected,
    })
  }

  const handleCellItemLeftClick = async (
    event: MouseEvent<HTMLDivElement>,
    item: ObjectDataRecord
  ) => {
    const actions = config.objectX.actions

    if (!actions?.length) {
      return
    }

    if (event.shiftKey && isSelectionAvailable()) {
      event.stopPropagation()

      handleChangeSelectedCells({
        columnId: params.field,
        rowId: params.id as string,
        endDate: dates[1],
        startDate: dates[0],
        rowData: params.row,
        cellId,
        objectXItems: params.value,
      })

      return
    }

    if ((event.ctrlKey || event.metaKey) && isSelectionAvailable()) {
      event.stopPropagation()

      handleToggleSelectedEvent({
        cellId,
        event: item,
        pkFieldName: objectXPKField?.name,
        columnId: params.field,
        rowId: params.id as string,
        endDate: dates[1],
        startDate: dates[0],
        rowData: params.row,
      })

      return
    }

    if (actions?.some(action => action.eventCode === EVENT_CODE.ON_CELL_VALUE_LEFT_CLICK)) {
      onClickWithDelay(event, () => {
        event.stopPropagation()

        handleExecuteActions({
          actions: config.objectX.actions || [],
          eventCode: EVENT_CODE.ON_CELL_VALUE_LEFT_CLICK,
          srcObj: {
            gridCellParams: params,
            cellItem: item,
            formProps: getFormProps(item),
            event,
            additionalProps: {
              addHistory: true,
            },
          },
        })
      })
    }
  }

  const handleCellItemDoubleLeftClick = async (
    event: MouseEvent<HTMLDivElement>,
    item: ObjectDataRecord
  ) => {
    const actions = config.objectX.actions

    if (!actions?.length) {
      return
    }

    if (actions?.some(action => action.eventCode === EVENT_CODE.ON_CELL_VALUE_DOUBLE_LEFT_CLICK)) {
      event.stopPropagation()

      onDoubleClick(event, () =>
        handleExecuteActions({
          actions: config.objectX.actions as ActionRow[],
          eventCode: EVENT_CODE.ON_CELL_VALUE_DOUBLE_LEFT_CLICK,
          srcObj: {
            cellItem: item,
            gridCellParams: params,
            formProps: getFormProps(item),
            event,
            additionalProps: {
              addHistory: true,
            },
          },
        })
      )
    }
  }

  const handleCellItemRightClick = async (
    event: MouseEvent<HTMLDivElement>,
    item: ObjectDataRecord
  ) => {
    if (!config.objectX.actions?.length) {
      return
    }

    if (
      objectXActionsObject[EVENT_CODE.ON_CELL_VALUE_RIGHT_CLICK]?.actionCode ===
      ACTION_CODE.OPEN_CONTEXT_MENU
    ) {
      event.stopPropagation()

      handleOpenContextMenu(event, objectXActionsObject[EVENT_CODE.ON_CELL_VALUE_RIGHT_CLICK], item)
    }
  }

  const handleCellItemMouseOver = (
    event: MouseEvent<HTMLDivElement>,
    item: ObjectDataRecord,
    isItemLoading: boolean
  ) => {
    if (
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      isItemLoading ||
      cellContextRef.current.isDraggingActive
    ) {
      return
    }

    if (
      objectXActionsObject?.[EVENT_CODE.ON_CELL_VALUE_MOUSE_OVER]?.actionCode ===
      ACTION_CODE.OPEN_TOOLTIP
    ) {
      event.stopPropagation()

      handleOpenTooltip(event, {
        srcObj: item,
        action: objectXActionsObject[EVENT_CODE.ON_CELL_VALUE_MOUSE_OVER],
      })
    }
  }

  const handleCellRightClick = async (event: MouseEvent<HTMLDivElement>) => {
    const { actions } = config.objectX

    if (!actions?.length) {
      return
    }

    if (
      objectXActionsObject[EVENT_CODE.ON_CELL_RIGHT_CLICK]?.actionCode ===
      ACTION_CODE.OPEN_CONTEXT_MENU
    ) {
      handleOpenContextMenu(event, objectXActionsObject[EVENT_CODE.ON_CELL_RIGHT_CLICK])
    }
  }

  const handleCellLeftClick = (event: MouseEvent<HTMLDivElement>) => {
    const { actions } = config.objectX

    if (!actions?.length) {
      return
    }

    if (event.shiftKey && isSelectionAvailable() && !cellContextRef.current.hasSelectionDragged) {
      event.stopPropagation()

      handleChangeSelectedCells({
        cellId,
        objectXItems: params.value,
        columnId: params.field,
        rowId: params.id as string,
        endDate: dates[1],
        startDate: dates[0],
        rowData: params.row,
      })

      return
    }

    if (cellContextRef.current.hasSelectionDragged) {
      return
    }

    if (actions?.some(action => action.eventCode === EVENT_CODE.ON_CELL_LEFT_CLICK)) {
      onClickWithDelay(event, () => {
        event.stopPropagation()
        handleExecuteActions({
          actions: config.objectX.actions as ActionRow[],
          eventCode: EVENT_CODE.ON_CELL_LEFT_CLICK,
          srcObj: {
            gridCellParams: params,
            formProps: getFormProps(),
            event,
            additionalProps: {
              addHistory: true,
            },
          },
        })
      })
    }
  }

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.shiftKey && event.button === 0 && isSelectionAvailable()) {
      event.preventDefault()
      cellContextRef.current.isSelectionDragging = true
      cellContextRef.current.hasSelectionDragged = false
      cellContextRef.current.dragStartCell = {
        cellId,
        events: params.value || [],
        columnId: params.field,
        rowId: params.id as string,
        endDate: dates[1],
        startDate: dates[0],
        rowData: params.row,
      }
    }
  }

  const handleMouseEnter = () => {
    if (
      cellContextRef.current.isSelectionDragging &&
      cellContextRef.current.dragStartCell &&
      cellContextRef.current.dragStartCell?.cellId !== cellId
    ) {
      if (!cellContextRef.current.hasSelectionDragged) {
        cellContextRef.current.hasSelectionDragged = true

        handleChangeSelectedCells({
          ...cellContextRef.current.dragStartCell,
          objectXItems: cellContextRef.current.dragStartCell?.events || [],
        })
      }

      handleChangeSelectedCells({
        cellId,
        objectXItems: params.value || [],
        columnId: params.field,
        rowId: params.id as string,
        endDate: dates[1],
        startDate: dates[0],
        rowData: params.row,
      })
    }
  }

  const handleMouseUp = () => {
    cellContextRef.current.isSelectionDragging = false
    cellContextRef.current.dragStartCell = null
    cellContextRef.current.hasSelectionDragged = false
  }

  const isSelectionAvailable = () => {
    return config.objectX.actions?.some(
      action =>
        action.eventCode === EVENT_CODE.ON_CELL_LEFT_CLICK_WITH_SHIFT_OR_CTRL &&
        action.actionCode === ACTION_CODE.SELECT_ITEM
    )
  }

  return {
    handleMouseDown,
    handleMouseUp,
    handleMouseEnter,
    handleCellItemDoubleLeftClick,
    handleCellRightClick,
    handleCellLeftClick,
    handleCellItemMouseOver,
    handleCellItemRightClick,
    handleCellItemLeftClick,
  }
}
